import { Button } from "@/components/ui/button";
import { MESSAGE } from "@/constants/message";
import { useModal } from "@/context/ModalProvider";
import { AnalysisJobItem } from "@/types/analysis";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import Divider from "../ui/Divider";
import LoadingWrapper from "../ui/LoadingWrapper";
import { AnalysisHistoryDatasetList } from "./AnalysisHistoryDatasetList";
import { EmptyState } from "./EmptyState";

interface DetailSectionProps {
  isLoading?: boolean;
  data?: AnalysisJobItem;
  emptyMessage: { title: string; desc: string };
  onDelete?: (id: string) => void;
  onOpen?: (id: string) => void;
  onRerun?: (id: string) => void;
}

export const DetailSection = ({
  isLoading,
  data,
  emptyMessage,
  onDelete,
  onOpen,
  onRerun
}: DetailSectionProps) => {
  const { showModal } = useModal();
  const handleOnOpen = () => {
    if (!data) return;

    const expired = dayjs(data.expired_at).isBefore(dayjs());
    if (expired) {
      showModal("confirm", {
        title: "Data Analysis Expired",
        message: MESSAGE.ANALYSIS_EXPIRED,
        confirmLabel: "Re-Run",
        cancelLabel: "Later",
        onCancel: () => {},
        onConfirm: () => {
          onRerun?.(data.id);
        }
      });
      return;
    }
    onOpen?.(data.id);
  };

  const handleOnDelete = () => {
    if (!data) return;

    showModal("confirm", {
      title: "Are you sure, you want to delete? ",
      message: "Once deleted, this action cannot be undone.",
      cancelLabel: "Cancel",
      confirmLabel: "Delete",
      onConfirm: () => onDelete?.(data.id)
    });
  };

  return (
    <div
      className={twMerge(
        "border border-whisper rounded-lg flex flex-col h-full overflow-hidden",
        data && "border-teal-200"
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "bg-white-smoke rounded-t-lg p-4 border-b border-whisper flex-shrink-0",
          data && "border-teal-200 bg-teal-50"
        )}
      >
        <div className="grid grid-cols-2 gap-2 text-txtHeading">
          <div className="flex items-center gap-12">
            <span className="font-semibold">Author Name</span>
            <span>{data?.created_by || "-"}</span>
          </div>
          <div className="flex items-center gap-12">
            <span className="font-semibold">Generated Time</span>
            <span>
              {data?.created_at
                ? dayjs(data.created_at).format("DD MMM YYYY, HH:mm:ss")
                : "-"}
            </span>
          </div>
        </div>
      </div>
      <LoadingWrapper
        isLoading={!!isLoading}
        loadingClassName="flex flex-col items-center justify-center text-center text-sm text-dim-gray gap-2 h-full"
        loadingText={<p className="text-txtPlaceholder text-xs">Loading...</p>}
      >
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {data ? (
            <>
              {/* Input / Output Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white-smoke rounded-lg text-txtHeading flex flex-col gap-4">
                  <p className="font-semibold text-[14px]">Spatial Settings</p>
                  <div className="grid grid-cols-[2fr_3fr]">
                    <p className="font-semibold">Input Area:</p>
                    <p>Administrative Area</p>
                  </div>
                  <div className="grid grid-cols-[2fr_3fr]">
                    <p className="font-semibold">Adm Area Selection:</p>
                    <p>Indonesia → DKI Jakarta → Jakarta Pusat</p>
                  </div>
                </div>

                <div className="p-4 bg-white-smoke rounded-lg text-txtHeading flex flex-col gap-4">
                  <p className="font-semibold text-[14px]">Output Analysis</p>
                  <div className="grid grid-cols-[2fr_3fr]">
                    <p className="font-semibold">Output Type:</p>
                    <p>H3</p>
                  </div>
                  <div className="grid grid-cols-[2fr_3fr]">
                    <p className="font-semibold">Resolution Settings:</p>
                    <p>Level 7 → 1.40Km (5.16km2)</p>
                  </div>
                </div>
              </div>

              <Divider className="bg-white-smoke w-full h-[1px] my-4" />

              {/* Dataset List */}
              <AnalysisHistoryDatasetList />
            </>
          ) : (
            <EmptyState
              title={emptyMessage.title}
              description={emptyMessage.desc}
            />
          )}
        </div>
      </LoadingWrapper>

      {/* Footer Buttons */}
      <div className="flex gap-2 shadow-2xl bg-white p-4 justify-center 2xl:justify-end">
        <Button
          variant="outline"
          className="w-[800px] 2xl:w-[400px] border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white rounded-xl"
          onClick={handleOnDelete}
          disabled={!data}
        >
          Delete
        </Button>
        <Button
          className="w-[800px] 2xl:w-[400px] bg-primary-teal hover:bg-primary-teal/90 text-white rounded-xl"
          onClick={handleOnOpen}
          disabled={!data}
        >
          Open
        </Button>
      </div>
    </div>
  );
};
